<script setup>
// KISOFT: busca inline (conversas/contatos) EM CIMA da lista de chats — NAO navega pra outra
// pagina; mostra os resultados num dropdown. Reusa o store conversationSearch do Chatwoot.
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { vOnClickOutside } from '@vueuse/components';

const router = useRouter();

const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const conversations = useMapGetter('conversationSearch/getConversationRecords');
const contacts = useMapGetter('conversationSearch/getContactRecords');
const messages = useMapGetter('conversationSearch/getMessageRecords');
const uiFlags = useMapGetter('conversationSearch/getUIFlags');

const q = ref('');
const open = ref(false);
let deb = null;

watch(q, val => {
  clearTimeout(deb);
  const term = (val || '').trim();
  // limpa SEMPRE (as mutacoes do store ACUMULAM -> senao o resultado antigo persiste)
  store.dispatch('conversationSearch/clearSearchResults');
  if (term.length < 2) {
    open.value = false;
    return;
  }
  open.value = true;
  deb = setTimeout(() => {
    store.dispatch('conversationSearch/fullSearch', { q: term });
  }, 300);
});

const convUrl = c => frontendURL(`accounts/${accountId.value}/conversations/${c.id}`);
const contactUrl = c => frontendURL(`accounts/${accountId.value}/contacts/${c.id}`);
const msgConvId = m => m.conversationId || m.conversation_id || (m.conversation && m.conversation.id);
const msgUrl = m =>
  frontendURL(`accounts/${accountId.value}/conversations/${msgConvId(m)}`, { messageId: m.id });
const msgSnippet = m => {
  const t = (m.content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return t.length > 80 ? t.slice(0, 80) + '…' : t || '(sem texto)';
};
// navega igual ao ConversationCard (router.push com o path do frontendURL) — RouterLink com
// string as vezes nao abria a conversa. Ctrl/Cmd+clique abre em nova aba.
const go = (path, e) => {
  if (!path) return;
  if (e && (e.metaKey || e.ctrlKey)) {
    window.open(`${window.chatwootConfig.hostURL}${path}`, '_blank', 'noopener,noreferrer');
    return;
  }
  open.value = false;
  router.push({ path });
};
const close = () => {
  open.value = false;
};
const clear = () => {
  q.value = '';
  open.value = false;
};
const onFocus = () => {
  if ((q.value || '').trim().length >= 2) open.value = true;
};
</script>

<template>
  <div v-on-click-outside="close" class="relative flex-grow min-w-0">
    <div
      class="flex items-center gap-2 px-2.5 h-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color"
    >
      <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
      <input
        v-model="q"
        type="text"
        placeholder="Buscar conversas ou contatos…"
        class="flex-grow min-w-0 bg-transparent border-0 outline-none text-sm text-n-slate-12 placeholder:text-n-slate-10"
        @focus="onFocus"
      >
      <button
        v-if="q"
        type="button"
        class="flex-shrink-0 i-lucide-x size-4 text-n-slate-10 hover:text-n-slate-12"
        @click="clear"
      />
    </div>

    <div
      v-if="open"
      class="absolute z-50 mt-1 w-full max-h-[60vh] overflow-y-auto rounded-lg border border-n-weak bg-n-solid-2 shadow-lg py-1"
    >
      <template v-if="conversations.length">
        <div class="px-3 pt-1.5 pb-1 text-xxs font-semibold uppercase tracking-wide text-n-slate-10">
          Conversas
        </div>
        <a
          v-for="c in conversations.slice(0, 6)"
          :key="'c' + c.id"
          :href="convUrl(c)"
          class="flex items-center gap-2 px-3 py-1.5 hover:bg-n-alpha-2 text-n-slate-12 cursor-pointer"
          @click.prevent="go(convUrl(c), $event)"
        >
          <span class="flex-shrink-0 i-lucide-message-circle size-4 text-n-slate-10" />
          <span class="flex-grow min-w-0 truncate text-sm">{{ (c.contact && c.contact.name) || 'Conversa' }}</span>
          <span class="flex-shrink-0 text-xxs text-n-slate-10">#{{ c.id }}</span>
        </a>
      </template>

      <template v-if="contacts.length">
        <div class="px-3 pt-1.5 pb-1 text-xxs font-semibold uppercase tracking-wide text-n-slate-10">
          Contatos
        </div>
        <a
          v-for="ct in contacts.slice(0, 6)"
          :key="'ct' + ct.id"
          :href="contactUrl(ct)"
          class="flex items-center gap-2 px-3 py-1.5 hover:bg-n-alpha-2 text-n-slate-12 cursor-pointer"
          @click.prevent="go(contactUrl(ct), $event)"
        >
          <span class="flex-shrink-0 i-lucide-user size-4 text-n-slate-10" />
          <span class="flex-grow min-w-0 truncate text-sm">{{ ct.name || 'Contato' }}</span>
          <span class="flex-shrink-0 text-xxs text-n-slate-10 truncate max-w-[45%]">{{ ct.email || ct.phone_number || '' }}</span>
        </a>
      </template>

      <template v-if="messages.length">
        <div class="px-3 pt-1.5 pb-1 text-xxs font-semibold uppercase tracking-wide text-n-slate-10">
          Mensagens
        </div>
        <a
          v-for="m in messages.slice(0, 6)"
          :key="'m' + m.id"
          :href="msgUrl(m)"
          class="flex items-start gap-2 px-3 py-1.5 hover:bg-n-alpha-2 text-n-slate-12 cursor-pointer"
          @click.prevent="go(msgUrl(m), $event)"
        >
          <span class="flex-shrink-0 mt-0.5 i-lucide-message-square-text size-4 text-n-slate-10" />
          <span class="flex-grow min-w-0">
            <span class="block text-xs font-medium truncate text-n-slate-11">{{ (m.sender && m.sender.name) || 'Mensagem' }}</span>
            <span class="block text-sm truncate">{{ msgSnippet(m) }}</span>
          </span>
        </a>
      </template>

      <div v-if="uiFlags.isFetching" class="px-3 py-2 text-sm text-n-slate-10">Buscando…</div>
      <div
        v-else-if="!conversations.length && !contacts.length && !messages.length"
        class="px-3 py-2 text-sm text-n-slate-10"
      >
        Nada encontrado.
      </div>
    </div>
  </div>
</template>
